import { createBrowserRouter, type RouteObject } from 'react-router';

import { CallbackPage } from '../auth/pages/CallbackPage';
import { DevLoginPage } from '../auth/pages/DevLoginPage';
import { LoginPage } from '../auth/pages/LoginPage';
import { RequireAuth } from '../auth/guards';
import { NotFoundPage, RouteError } from '../components/RouteError';
import { AdminLayout } from '../layouts/AdminLayout';
import { PortalLayout } from '../layouts/PortalLayout';
import { areaModules } from './areas';

/**
 * THE ROUTER — assembled from the area registry.
 *
 * ⚠ MODULE AGENTS: do not edit this file. Add routes to
 * `src/areas/<your-module>/routes.tsx`; they are picked up here automatically.
 *
 * Shape:
 *   /login, /auth/callback, /dev-login   public
 *   /*                                   RequireAuth → AdminLayout  → area `routes`
 *   /portal/*                            RequireAuth → PortalLayout → area `portalRoutes`
 *
 * There is no permission guard on the route tree itself: an area that is fully
 * gated wraps its own children in `<RequirePermission>` (see `auth/guards.tsx`).
 * Route-level gating in one central place would be another shared file every
 * module agent has to edit.
 */

function adminRoutes(): RouteObject[] {
  return areaModules.flatMap((area) => area.routes ?? []);
}

function portalRoutes(): RouteObject[] {
  return areaModules.flatMap((area) => area.portalRoutes ?? []);
}

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  // Alias used by the Android scanner's Custom Tab (see auth/android-bridge.ts).
  { path: '/auth/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/auth/callback', element: <CallbackPage />, errorElement: <RouteError /> },
  { path: '/dev-login', element: <DevLoginPage />, errorElement: <RouteError /> },

  {
    path: '/portal',
    element: (
      <RequireAuth>
        <PortalLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [...portalRoutes(), { path: '*', element: <NotFoundPage /> }],
  },

  {
    path: '/',
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [...adminRoutes(), { path: '*', element: <NotFoundPage /> }],
  },
];

export function createRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes);
}
